import { providerCodesForBaggageFilter } from '@pages/FlightSearchResult/constants';
import type { LegViewModel } from 'flights/legViewModel';
import type { TripViewModel } from 'flights/tripViewModel';

import {
  isTripViewModelWithinSelectedTimeBlocksFilter,
  matchFareFilterCriteria,
  checkIfTripViewModelContainsSelectedLegs,
} from '@helpers/flights/flightFilters';

type FlightFilterParams = {
  tripViewModels: Array<TripViewModel>;
  selectedPaymentMethodIds: Array<number>;
  selectedStopCodes: Array<FlightMulticityFilterState<number>>; // 0, 1, 2
  selectedProviders: Array<string>;
  selectedFareProviderCodes: Array<string>;
  selectedFlightExperiences: Array<FlightMulticityFilterState<string>>;
  selectedAirlineCodes: Array<FlightMulticityFilterState<string>>;
  selectedAllianceCodes: Array<string>;
  selectedTripOptions: Array<string>;
  selectedOriginAirportCodes: Array<string>;
  selectedDestinationAirportCodes: Array<string>;
  selectedStopoverAirportCodes: Array<FlightMulticityFilterState<string>>;
  selectedStopoverOptions: Array<string>;
  selectedStopoverDuration: Array<FlightMulticityFilterState<number>>;
  selectedDepartDuration: Array<FlightMulticityFilterState<number>>;
  selectedReturnDuration: Array<number>;
  selectedDepartLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedDepartLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedReturnLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedReturnLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedUsdPrices: Array<number>;
  priceOptions: PriceOptions;
  selectedLegs: Array<{ index: number; legId: string }>;
  isApplyBaggageFilter: boolean;
};

export function filterOnewayAndRoundTripViewModelBasedOnFilters({
  tripViewModels,
  selectedPaymentMethodIds,
  selectedStopCodes,
  selectedProviders,
  selectedFareProviderCodes,
  selectedFlightExperiences,
  selectedAirlineCodes,
  selectedAllianceCodes,
  selectedTripOptions,
  selectedOriginAirportCodes,
  selectedDestinationAirportCodes,
  selectedStopoverAirportCodes,
  selectedStopoverOptions,
  selectedStopoverDuration,
  selectedDepartDuration,
  selectedReturnDuration,
  selectedDepartLegDepartureTimeBlocks,
  selectedDepartLegArrivalTimeBlocks,
  selectedReturnLegDepartureTimeBlocks,
  selectedReturnLegArrivalTimeBlocks,
  selectedUsdPrices,
  priceOptions,
  selectedLegs,
  isApplyBaggageFilter,
}: FlightFilterParams): Array<TripViewModel> {
  const isBookOnWego = selectedProviders.includes('wego');
  const isBookWithAirlines = selectedProviders.includes('airline');

  const isNoChangeOfAirportAtStopover = selectedStopoverOptions.includes('same_airport');

  return tripViewModels.filter((tripViewModel) => {
    const departureLegViewModel = tripViewModel.legs[0];
    const returnLegViewModel = tripViewModel.legs[1] || undefined;

    const fareViewModels = tripViewModel.fares;

    const paymentMethodFilter = applyPaymentMethodFilter(selectedPaymentMethodIds, fareViewModels);

    const stopCodesFilter = applyStopCodesFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedStopCodes,
    );

    const flightExperienceFilter = applyFlightExperienceFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedFlightExperiences,
    );

    const providerFilter = applyProviderFilter(
      tripViewModel,
      selectedFareProviderCodes,
      isBookOnWego,
      isBookWithAirlines,
      isApplyBaggageFilter,
    );

    const airlineFilter = applyAirlineFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedAirlineCodes,
      selectedTripOptions,
    );

    const allianceCodesFilter = applyAllianceCodesFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedAllianceCodes,
    );

    const airportCodesFilter = applyAirportCodesFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedOriginAirportCodes,
      selectedDestinationAirportCodes,
    );

    const stopoverFilter = applyStopoverFilter(
      departureLegViewModel,
      returnLegViewModel,
      selectedStopoverAirportCodes,
      isNoChangeOfAirportAtStopover,
    );

    const durationFilter = applyDurationFilters(
      departureLegViewModel,
      returnLegViewModel,
      selectedStopoverDuration,
      selectedDepartDuration,
      selectedReturnDuration,
    );

    const flightTimeFilter = applyFlightTimeFilters(
      tripViewModel,
      selectedDepartLegDepartureTimeBlocks,
      selectedDepartLegArrivalTimeBlocks,
      selectedReturnLegDepartureTimeBlocks,
      selectedReturnLegArrivalTimeBlocks,
    );

    const priceFilter = applyPriceFilter(fareViewModels[0]?.fare, selectedUsdPrices, priceOptions);

    const legFilter = checkIfTripViewModelContainsSelectedLegs(tripViewModel, selectedLegs);

    return (
      paymentMethodFilter &&
      stopCodesFilter &&
      providerFilter &&
      flightExperienceFilter &&
      airlineFilter &&
      allianceCodesFilter &&
      airportCodesFilter &&
      stopoverFilter &&
      durationFilter &&
      flightTimeFilter &&
      priceFilter &&
      legFilter
    );
  });
}

// Helper function to check selected legs
export function applyPaymentMethodFilter(
  selectedPaymentMethodIds: Array<number>,
  fareViewModels: FareViewModel[],
): boolean {
  return selectedPaymentMethodIds.length > 0
    ? selectedPaymentMethodIds.some((paymentMethod) =>
        fareViewModels.some((fareViewModel) =>
          fareViewModel.fare.paymentFees.some(
            (paymentFee) => paymentFee.paymentMethodId === paymentMethod,
          ),
        ),
      )
    : true;
}

export function applyStopCodesFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedStopCodes: Array<FlightMulticityFilterState<number>>,
): boolean {
  const departureLegStopoversCount = Math.min(departureLegViewModel.leg.stopoversCount, 2);
  const returnLegStopoversCount = returnLegViewModel
    ? Math.min(returnLegViewModel.leg.stopoversCount, 2)
    : -1;
  const highestCountOfBothLeg = Math.max(departureLegStopoversCount, returnLegStopoversCount);

  const onewayAndRoundTripSelectedStopCode = selectedStopCodes[0]?.values || [];
  return onewayAndRoundTripSelectedStopCode.length > 0
    ? onewayAndRoundTripSelectedStopCode.some(
        (selectedStopCodeNumber) => selectedStopCodeNumber === highestCountOfBothLeg,
      )
    : true;
}

export function applyProviderFilter(
  tripViewModel: TripViewModel,
  selectedFareProviderCodes: Array<string>,
  isBookOnWego: boolean,
  isBookWithAirlines: boolean,
  isApplyBaggageFilter?: boolean,
): boolean {
  const fareViewModels = tripViewModel.fares;

  let matchProvidersAndProviderCodesFilter = true;
  if (
    selectedFareProviderCodes.length > 0 ||
    isBookOnWego ||
    isBookWithAirlines ||
    isApplyBaggageFilter
  ) {
    const fareProviderCodes = isApplyBaggageFilter
      ? selectedFareProviderCodes.concat(providerCodesForBaggageFilter)
      : selectedFareProviderCodes;

    matchProvidersAndProviderCodesFilter = fareViewModels.some(({ provider }) => {
      return matchFareFilterCriteria(provider, fareProviderCodes, isBookOnWego, isBookWithAirlines);
    });

    const matchedFares = fareViewModels.filter(({ provider }) =>
      matchFareFilterCriteria(provider, fareProviderCodes, isBookOnWego, isBookWithAirlines),
    );

    tripViewModel.filteredFares = matchedFares;
  } else {
    tripViewModel.filteredFares = fareViewModels;
  }

  return matchProvidersAndProviderCodesFilter;
}

export function applyFlightExperienceFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedFlightExperiences: Array<FlightMulticityFilterState<string>>,
): boolean {
  const onewayAndRoundTripSelectedFlightExperience = selectedFlightExperiences[0]?.values || [];

  const isNotOvernightFlight =
    onewayAndRoundTripSelectedFlightExperience.includes('no_overnight_flight');

  const isNotOvernightFlightFilterSatisfied = isNotOvernightFlight
    ? !departureLegViewModel.leg.overnight &&
      (returnLegViewModel ? !returnLegViewModel.leg.overnight : true)
    : true;

  const isNotLongStopoverFlight =
    onewayAndRoundTripSelectedFlightExperience.includes('no_long_stopover');

  const isNotLongStopoverFlightFilterSatisfied = isNotLongStopoverFlight
    ? !departureLegViewModel.leg.longStopover &&
      (returnLegViewModel ? !returnLegViewModel.leg.longStopover : true)
    : true;

  return isNotOvernightFlightFilterSatisfied && isNotLongStopoverFlightFilterSatisfied;
}

export function applyAirlineFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedAirlineCodes: FlightMulticityFilterState<string>[],
  selectedTripOptions: string[],
): boolean {
  const onewayAndRoundTripSelectedAirlineCodes = selectedAirlineCodes[0]?.values || [];

  const isTripWithSameAirline = selectedTripOptions.includes('same_airline');
  let tripWithSameAirlineFilter = true;
  if (isTripWithSameAirline) {
    const departureLegAirportCodes = departureLegViewModel.leg.airlineCodes;
    const returnLegAirportCodes = returnLegViewModel
      ? returnLegViewModel.leg.airlineCodes
      : undefined;

    tripWithSameAirlineFilter =
      departureLegAirportCodes.length === 1 &&
      (returnLegAirportCodes
        ? returnLegAirportCodes.length === 1 &&
          departureLegAirportCodes[0] === returnLegAirportCodes[0]
        : true);
  }

  return (
    tripWithSameAirlineFilter &&
    (onewayAndRoundTripSelectedAirlineCodes.length > 0
      ? onewayAndRoundTripSelectedAirlineCodes.some((selectedAirlineCode) =>
          departureLegViewModel.leg.airlineCodes
            .map((code) => code.toLowerCase())
            .includes(selectedAirlineCode.toLowerCase()),
        ) &&
        (returnLegViewModel
          ? onewayAndRoundTripSelectedAirlineCodes.some((selectedAirlineCode) =>
              returnLegViewModel.leg.airlineCodes
                .map((code) => code.toLowerCase())
                .includes(selectedAirlineCode.toLowerCase()),
            )
          : true)
      : true)
  );
}

export function applyAllianceCodesFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedAllianceCodes: Array<string>,
): boolean {
  return selectedAllianceCodes.length > 0
    ? selectedAllianceCodes.some((selectedAllianceCode) =>
        departureLegViewModel.leg.allianceCodes.includes(selectedAllianceCode),
      ) &&
        (returnLegViewModel
          ? selectedAllianceCodes.some((selectedAllianceCode) =>
              returnLegViewModel.leg.allianceCodes.includes(selectedAllianceCode),
            )
          : true)
    : true;
}

export function applyAirportCodesFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedOriginAirportCodes: Array<string>,
  selectedDestinationAirportCodes: Array<string>,
): boolean {
  const originAirportCodesMatch =
    selectedOriginAirportCodes.length > 0
      ? selectedOriginAirportCodes.some(
          (selectedOriginAirportCode) =>
            departureLegViewModel.leg.departureAirportCode.toLowerCase() ===
            selectedOriginAirportCode.toLowerCase(),
        ) &&
        (returnLegViewModel
          ? selectedOriginAirportCodes.some(
              (selectedOriginAirportCode) =>
                returnLegViewModel.leg.arrivalAirportCode.toLowerCase() ===
                selectedOriginAirportCode.toLowerCase(),
            )
          : true)
      : true;

  const destinationAirportCodesMatch =
    selectedDestinationAirportCodes.length > 0
      ? selectedDestinationAirportCodes.some(
          (selectedDestinationAirportCode) =>
            departureLegViewModel.leg.arrivalAirportCode.toLowerCase() ===
            selectedDestinationAirportCode.toLowerCase(),
        ) &&
        (returnLegViewModel
          ? selectedDestinationAirportCodes.some(
              (selectedDestinationAirportCode) =>
                returnLegViewModel.leg.departureAirportCode.toLowerCase() ===
                selectedDestinationAirportCode.toLowerCase(),
            )
          : true)
      : true;

  return originAirportCodesMatch && destinationAirportCodesMatch;
}

export function applyStopoverFilter(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedStopoverAirportCodes: FlightMulticityFilterState<string>[],
  isNoChangeOfAirportAtStopover: boolean,
): boolean {
  const onewayAndRoundTripSelectedStopoverAirportCodes =
    selectedStopoverAirportCodes[0]?.values || [];

  const stopoverAirportCodesFilter =
    onewayAndRoundTripSelectedStopoverAirportCodes.length > 0
      ? onewayAndRoundTripSelectedStopoverAirportCodes.some((selectedStopoverAirportCode) =>
          departureLegViewModel.leg.stopoverAirportCodes.includes(selectedStopoverAirportCode),
        ) ||
        (returnLegViewModel
          ? onewayAndRoundTripSelectedStopoverAirportCodes.some((selectedStopoverAirportCode) =>
              returnLegViewModel.leg.stopoverAirportCodes.includes(selectedStopoverAirportCode),
            )
          : false)
      : true;

  let noChangeOfAirportAtStopoverFilter = true;
  if (isNoChangeOfAirportAtStopover) {
    let isNoChangeOfAirportAtStopoverForDepartureLeg = true;
    let isNoChangeOfAirportAtStopoverForReturnLeg = true;

    if (departureLegViewModel.segmentViewModels.length > 1) {
      for (let i = 0; i < departureLegViewModel.segmentViewModels.length - 1; i++) {
        const currentSegment = departureLegViewModel.segmentViewModels[i].segment;
        const nextSegment = departureLegViewModel.segmentViewModels[i + 1].segment;

        if (currentSegment.arrivalAirportCode !== nextSegment.departureAirportCode) {
          isNoChangeOfAirportAtStopoverForDepartureLeg = false;
          break;
        }
      }
    }

    if (!!returnLegViewModel && returnLegViewModel.segmentViewModels.length > 1) {
      for (let i = 0; i < returnLegViewModel.segmentViewModels.length - 1; i++) {
        const currentSegment = returnLegViewModel.segmentViewModels[i].segment;
        const nextSegment = returnLegViewModel.segmentViewModels[i + 1].segment;

        if (currentSegment.arrivalAirportCode !== nextSegment.departureAirportCode) {
          isNoChangeOfAirportAtStopoverForReturnLeg = false;
          break;
        }
      }
    }

    noChangeOfAirportAtStopoverFilter =
      isNoChangeOfAirportAtStopoverForDepartureLeg && isNoChangeOfAirportAtStopoverForReturnLeg;
  }

  return stopoverAirportCodesFilter && noChangeOfAirportAtStopoverFilter;
}

export function applyDurationFilters(
  departureLegViewModel: LegViewModel,
  returnLegViewModel: LegViewModel | undefined,
  selectedStopoverDuration: FlightMulticityFilterState<number>[],
  selectedDepartDuration: FlightMulticityFilterState<number>[],
  selectedReturnDuration: number[],
): boolean {
  const onewayAndRoundTripSelectedDepartDuration = selectedDepartDuration[0]?.values || [];
  const onewayAndRoundTripSelectedStopoverDuration = selectedStopoverDuration[0]?.values || [];

  let stopoverDuration = true;
  if (onewayAndRoundTripSelectedStopoverDuration.length === 2) {
    const highestStopoverDurationOfBothLeg = Math.max(
      departureLegViewModel.leg.stopoverDurationMinutes,
      returnLegViewModel ? returnLegViewModel.leg.stopoverDurationMinutes : 0,
    );

    stopoverDuration =
      highestStopoverDurationOfBothLeg >= onewayAndRoundTripSelectedStopoverDuration[0] &&
      highestStopoverDurationOfBothLeg <= onewayAndRoundTripSelectedStopoverDuration[1];
  }

  let departDurationFilter = true;
  if (onewayAndRoundTripSelectedDepartDuration.length === 2) {
    departDurationFilter =
      departureLegViewModel.leg.durationMinutes >= onewayAndRoundTripSelectedDepartDuration[0] &&
      departureLegViewModel.leg.durationMinutes <= onewayAndRoundTripSelectedDepartDuration[1];
  }

  let returnDurationFilter = true;
  if (!!returnLegViewModel && selectedReturnDuration.length === 2) {
    returnDurationFilter =
      returnLegViewModel.leg.durationMinutes >= selectedReturnDuration[0] &&
      returnLegViewModel.leg.durationMinutes <= selectedReturnDuration[1];
  }

  return stopoverDuration && departDurationFilter && returnDurationFilter;
}

export function applyFlightTimeFilters(
  tripViewModel: TripViewModel,
  selectedDepartLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedDepartLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedReturnLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedReturnLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
): boolean {
  const departLegDepartureTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedDepartLegDepartureTimeBlocks,
    'departLegDepartureTime',
  );

  const departLegArrivalTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedDepartLegArrivalTimeBlocks,
    'departLegArrivalTime',
  );

  const returnLegDepartureTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedReturnLegDepartureTimeBlocks,
    'returnLegDepartureTime',
  );

  const returnLegArrivalTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedReturnLegArrivalTimeBlocks,
    'returnLegArrivalTime',
  );

  return (
    departLegDepartureTimeBlockFilter &&
    departLegArrivalTimeBlockFilter &&
    returnLegDepartureTimeBlockFilter &&
    returnLegArrivalTimeBlockFilter
  );
}

export function applyPriceFilter(
  fare: FlightSearchResultFare,
  selectedUsdPrices: Array<number>,
  priceOptions: PriceOptions,
): boolean {
  let priceFilter = true;
  if (selectedUsdPrices.length === 2) {
    if (fare) {
      const { amountUsd, totalAmountUsd } = fare.price;

      let calculatedAmount = 0;

      if (priceOptions.showTotalPrice) {
        calculatedAmount = totalAmountUsd;
      } else {
        calculatedAmount = amountUsd;
      }

      priceFilter =
        !!fare &&
        calculatedAmount >= selectedUsdPrices[0] &&
        calculatedAmount <= selectedUsdPrices[1];
    }
  }

  return priceFilter;
}
